/**
 * FreelanceServicesSection - Service cards for Freelance & Gigs category
 * Follows the wireframe specs with anchor cards, triggers, and mobile sticky
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Star, CheckCircle, ArrowRight, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service Ad Card Component (matching existing ServiceAdCard pattern)
const FreelanceServiceCard = ({ service, onSelect, isAnchor = false }) => {
  const colorClasses = {
    blue: 'border-blue-200 hover:border-blue-400',
    purple: 'border-purple-200 hover:border-purple-400',
    emerald: 'border-emerald-200 hover:border-emerald-400',
    teal: 'border-teal-200 hover:border-teal-400',
    amber: 'border-amber-200 hover:border-amber-400',
    slate: 'border-slate-200 hover:border-slate-400',
    red: 'border-red-200 hover:border-red-400',
  };

  return (
    <Card 
      className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg cursor-pointer ${colorClasses[service.color] || 'border-gray-200'} ${isAnchor ? 'ring-2 ring-blue-500' : ''}`}
      onClick={() => onSelect?.(service)}
      data-testid={`service-card-${service.id}`}
    >
      <CardContent className="p-5">
        {/* Badge Type */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-blue-600 text-xs font-medium">🔹 {service.badge_type}</span>
        </div>

        {/* Service Name & Subtitle */}
        <div className="flex items-start gap-3 mb-3">
          <span className="text-2xl">{service.icon}</span>
          <div>
            <h3 className="font-semibold text-gray-900">{service.name}</h3>
            <p className="text-sm text-gray-500">{service.subtitle}</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1.5 mb-4">
          {service.features?.slice(0, 2).map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              {feature}
            </div>
          ))}
        </div>

        {/* Rating & Price */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1 text-sm">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span className="font-medium">{service.rating}</span>
            <span className="text-gray-400">({service.reviews?.toLocaleString()} {service.category === 'employer' ? 'clients' : 'freelancers'})</span>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-500">From </span>
            <span className="font-bold text-lg">£{service.price || service.default_price_gbp}</span>
            {service.per_unit && <span className="text-xs text-gray-500"> / {service.per_unit}</span>}
          </div>
        </div>

        {/* CTA Button */}
        <Button 
          className="w-full" 
          variant={isAnchor ? "default" : "outline"}
          data-testid={`service-cta-${service.id}`}
        >
          {service.cta_text}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        {/* Badge Earned Tag */}
        {service.badge_earned && (
          <div className="mt-3 flex items-center gap-1 text-xs text-emerald-600">
            <span>🏅</span>
            <span>Earns "{service.badge_earned}" badge</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Inline Nudge Component
const InlineNudge = ({ nudge, onAction }) => {
  return (
    <div 
      className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100"
      data-testid="inline-nudge"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{nudge.icon}</span>
        <p className="text-sm text-gray-700">{nudge.message}</p>
      </div>
      <Button size="sm" onClick={() => onAction?.(nudge.service)}>
        {nudge.cta}
      </Button>
    </div>
  );
};

// Mobile Sticky Component
const MobileStickyPill = ({ config, onOpen }) => {
  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:hidden z-50"
      data-testid="mobile-sticky"
    >
      <button
        onClick={onOpen}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all"
      >
        <div className="flex items-center gap-2">
          <span>{config.icon}</span>
          <span className="font-medium">{config.title}</span>
        </div>
        <span className="text-sm opacity-90">From £{config.from_price}</span>
      </button>
    </div>
  );
};

// Mobile Bottom Sheet Content
const MobileServiceSheet = ({ services, isOpen, onClose, onSelectService }) => {
  const freelancerServices = services.filter(s => s.category === 'candidate');
  const clientServices = services.filter(s => s.category === 'employer');

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
        <SheetHeader className="text-left pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <span>💼</span>
            Freelance & Gigs Services
          </SheetTitle>
        </SheetHeader>
        
        <div className="py-4 space-y-6 overflow-y-auto max-h-[55vh]">
          {/* Freelancer Services */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">For Freelancers</h4>
            <div className="space-y-2">
              {freelancerServices.slice(0, 4).map((service) => (
                <button
                  key={service.id}
                  onClick={() => onSelectService(service)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{service.icon}</span>
                    <span className="font-medium text-sm">{service.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">£{service.default_price_gbp}</span>
                    <Button size="sm" variant="ghost" className="text-blue-600">
                      {service.cta_text?.split(' ')[0] || 'Start'}
                    </Button>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Client Services */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">For Clients</h4>
            <div className="space-y-2">
              {clientServices.slice(0, 3).map((service) => (
                <button
                  key={service.id}
                  onClick={() => onSelectService(service)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{service.icon}</span>
                    <span className="font-medium text-sm">{service.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">£{service.default_price_gbp}</span>
                    <Button size="sm" variant="ghost" className="text-blue-600">
                      {service.per_unit || 'Start'}
                    </Button>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Main Component
const FreelanceServicesSection = ({ 
  placement = 'search', // 'search' | 'detail' | 'inline'
  showMobileSticky = true,
  triggerType = null,
  onServiceSelect,
  maxCards = 4,
}) => {
  const [services, setServices] = useState([]);
  const [triggers, setTriggers] = useState({});
  const [mobileConfig, setMobileConfig] = useState(null);
  const [inlineNudges, setInlineNudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/job-services/services/freelance-gigs`);
      const data = await response.json();
      
      if (data.success) {
        // Convert services object to array
        const servicesArray = Object.entries(data.services || {}).map(([id, service]) => ({
          id,
          ...service,
        }));
        setServices(servicesArray);
        setTriggers(data.triggers || {});
        setMobileConfig(data.mobile_sticky || null);
        setInlineNudges(Object.values(data.inline_nudges || {}));
      }
    } catch (error) {
      console.error('Error fetching freelance services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (service) => {
    onServiceSelect?.(service);
    setMobileSheetOpen(false);
  };

  // Get anchor service (primary)
  const anchorService = services.find(s => s.is_anchor);
  
  // Get candidate and employer services
  const candidateServices = services.filter(s => s.category === 'candidate' && !s.subcategory);
  const employerServices = services.filter(s => s.category === 'employer' && !s.subcategory);

  // Determine which services to show based on placement
  const getDisplayServices = () => {
    if (placement === 'search') {
      // Top of search: Anchor + 2 more candidate services
      return [
        anchorService,
        ...candidateServices.filter(s => !s.is_anchor).slice(0, 2),
      ].filter(Boolean);
    } else if (placement === 'detail') {
      // Gig detail page: Mix of services
      return [...candidateServices.slice(0, 2), ...employerServices.slice(0, 1)].filter(Boolean);
    } else if (placement === 'inline') {
      // Inline after gigs: Just 1-2 services
      return candidateServices.slice(0, 2);
    }
    return candidateServices.slice(0, maxCards);
  };

  // Get triggered services if a trigger type is specified
  const getTriggeredServices = () => {
    if (!triggerType || !triggers[triggerType]) return null;
    
    const trigger = triggers[triggerType];
    const triggeredServiceIds = trigger.services || [];
    const triggeredServices = services.filter(s => triggeredServiceIds.includes(s.id));
    
    return {
      services: triggeredServices,
      message: trigger.message,
      cta: trigger.cta,
    };
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-48 bg-gray-100 rounded-lg"></div>
        <div className="h-48 bg-gray-100 rounded-lg"></div>
      </div>
    );
  }

  const displayServices = getDisplayServices();
  const triggered = getTriggeredServices();

  return (
    <div className="space-y-6" data-testid="freelance-services-section">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span>💼</span>
            Freelance & Gigs Services
          </h2>
          <p className="text-sm text-gray-500">Stand out to clients with professional preparation</p>
        </div>
        <Badge variant="outline" className="text-xs">
          Sponsored
        </Badge>
      </div>

      {/* Triggered Service Nudge */}
      {triggered && triggered.services.length > 0 && (
        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
          <p className="text-sm font-medium text-amber-800 mb-3">{triggered.message}</p>
          <div className="flex flex-wrap gap-2">
            {triggered.services.map((service) => (
              <Button 
                key={service.id} 
                size="sm" 
                variant="outline"
                onClick={() => handleServiceSelect(service)}
                className="border-amber-300 hover:bg-amber-100"
              >
                {service.icon} {service.name} · £{service.default_price_gbp}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Inline Nudge (for inline placement) */}
      {placement === 'inline' && inlineNudges.length > 0 && (
        <InlineNudge 
          nudge={inlineNudges[0]} 
          onAction={(serviceId) => {
            const service = services.find(s => s.id === serviceId);
            if (service) handleServiceSelect(service);
          }}
        />
      )}

      {/* Service Cards Grid */}
      <div className={`grid gap-4 ${placement === 'inline' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {displayServices.map((service) => (
          <FreelanceServiceCard
            key={service.id}
            service={service}
            isAnchor={service.is_anchor}
            onSelect={handleServiceSelect}
          />
        ))}
      </div>

      {/* Employer Section (if on search page) */}
      {placement === 'search' && employerServices.length > 0 && (
        <div className="mt-8">
          <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span>🏢</span>
            For Clients Hiring Freelancers
          </h3>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            {employerServices.slice(0, 2).map((service) => (
              <FreelanceServiceCard
                key={service.id}
                service={service}
                onSelect={handleServiceSelect}
              />
            ))}
          </div>
        </div>
      )}

      {/* Mobile Sticky */}
      {showMobileSticky && mobileConfig && (
        <MobileStickyPill 
          config={mobileConfig}
          onOpen={() => setMobileSheetOpen(true)}
        />
      )}

      {/* Mobile Sheet */}
      <MobileServiceSheet
        services={services}
        isOpen={mobileSheetOpen}
        onClose={() => setMobileSheetOpen(false)}
        onSelectService={handleServiceSelect}
      />
    </div>
  );
};

export default FreelanceServicesSection;
export { FreelanceServiceCard, InlineNudge, MobileStickyPill };
